import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader } from "../components/Crud";
import CurrencyPicker from "../components/CurrencyPicker";
import { useFormat } from "../contexts/PrefsContext";
import { Check, ExternalLink, Loader2, Sparkles } from "lucide-react";

export default function Billing() {
    const { t } = useTranslation();
    const fmt = useFormat();
    const [bs, setBs] = useState(null);
    const [loading, setLoading] = useState(false);
    const [portalBusy, setPortalBusy] = useState(false);

    useEffect(() => { api.get("/billing/status").then(r => setBs(r.data)); }, []);

    const subscribe = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
            window.location.href = data.url;
        } catch (e) {
            toast.error("Could not start checkout", {
                description: e.response?.data?.detail || e.message,
            });
            setLoading(false);
        }
    };
    const cancel = async () => {
        if (!window.confirm("Cancel your subscription? You'll still have access until the end of the period.")) return;
        await api.post("/billing/cancel");
        const { data } = await api.get("/billing/status"); setBs(data);
    };
    const openPortal = async () => {
        setPortalBusy(true);
        try {
            const { data } = await api.post("/billing/portal");
            window.location.href = data.url;
        } catch (e) {
            toast.error("Could not open the customer portal", {
                description: e.response?.data?.detail || e.message,
            });
            setPortalBusy(false);
        }
    };

    if (!bs) return <div className="p-6">Loading…</div>;
    const isActive = bs.subscription_status === "active";

    return (
        <div data-testid="billing-page">
            <PageHeader
                title={t("billing.title")}
                subtitle={t("billing.subtitle")}
                action={<CurrencyPicker testid="billing-currency-picker" />}
            />
            <div className="card-warm p-8 max-w-xl">
                <div className="font-script text-xl text-[var(--brand-orange)]">{t("billing.currentPlan")}</div>
                <div className="font-serif text-4xl capitalize">{bs.plan}</div>
                <div className="mt-2 text-2xl">
                    <span className="font-mono" data-testid="billing-price">{fmt.fmtCurrency(bs.plan_price_gbp, "GBP")}</span>
                    <span className="text-sm text-[var(--text-ink)]/65"> {t("billing.perMonth")}</span>
                </div>
                <div className="text-[11px] text-[var(--text-ink)]/55 mt-0.5">
                    {t("billing.billedIn", { currency: "GBP" })} · {t("settings.currencyHelp")}
                </div>
                <div className="mt-4 text-sm text-[var(--text-ink)]/75">
                    Status: <span className={`px-2 py-0.5 rounded-full text-xs ${isActive ? "bg-[var(--status-green)]/15 text-[var(--status-green)]" : "bg-[var(--status-amber)]/20 text-[#8a6500]"}`}>{bs.subscription_status}</span>
                    {bs.days_left > 0 && bs.subscription_status === "trialing" &&
                        <span className="ml-3">· {t("billing.trialEnds", { count: bs.days_left })}</span>}
                </div>
                {bs.installer_perk_active && (
                    <div
                        data-testid="installer-perk-badge"
                        className="mt-4 rounded-xl p-3 border border-[var(--brand-magenta)]/25 bg-gradient-to-r from-[var(--brand-purple)]/8 via-[var(--brand-magenta)]/8 to-[var(--brand-orange)]/8"
                    >
                        <div className="flex items-start gap-2">
                            <Sparkles size={16} className="mt-0.5 text-[var(--brand-magenta)] shrink-0" />
                            <div className="min-w-0">
                                <div className="font-serif font-semibold text-sm">
                                    Installer perk active — {bs.installer_perk_percent}% off your first paid month
                                </div>
                                <div className="text-xs text-[var(--text-ink)]/65 mt-0.5">
                                    Thanks for installing Virtually Unstoppable to your home screen. The discount is applied automatically when you start your subscription.
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <ul className="mt-5 space-y-1.5 text-sm">
                    {["7-day free trial", "Cancel anytime", "All features unlocked",
                      bs.plan === "student" ? "Reference Checker + Student Finance"
                                            : "AI Contract Writer + Client Memory + Meeting Hub"].map(f => (
                        <li key={f} className="flex items-center gap-2">
                            <Check size={14} className="text-[var(--status-green)]" /> {f}
                        </li>
                    ))}
                </ul>
                <div className="mt-6 flex flex-wrap gap-2">
                    {!isActive ? (
                        <button onClick={subscribe} disabled={loading} data-testid="subscribe-btn" className="btn-primary">
                            {loading ? "Redirecting…" : "Start subscription"}
                        </button>
                    ) : (
                        <>
                            <button onClick={openPortal} disabled={portalBusy} data-testid="manage-sub-btn"
                                    className="btn-primary inline-flex items-center gap-2">
                                {portalBusy ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                                {portalBusy ? "Opening Stripe…" : "Manage subscription"}
                            </button>
                            <button onClick={cancel} data-testid="cancel-sub-btn" className="btn-ghost">
                                Cancel subscription
                            </button>
                        </>
                    )}
                </div>
                {isActive && (
                    <div className="mt-3 text-xs text-[var(--text-ink)]/55">
                        Update your card, view invoices and cancel from the secure Stripe Customer Portal.
                    </div>
                )}
            </div>
        </div>
    );
}
